import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Server-side pagination hook for API calls that support pagination.
 * 
 * @param {Function} fetchFn - Async function that takes (page, pageSize, filters) and returns { data/items, total }
 * @param {Object} options - Configuration options
 * @param {number} options.pageSize - Items per page (default: 20)
 * @param {Array} options.dependencies - Additional dependencies to trigger refetch
 * @param {boolean} options.enabled - Whether to fetch data (default: true)
 * 
 * @returns {Object} { data, pagination, loading, error, reload }
 */
export function useServerPagination(fetchFn, options = {}) {
  const { 
    pageSize = 20, 
    enabled = true 
  } = options;

  const [page, setPage] = useState(1);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    if (!enabled) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await fetchFn(page, pageSize);
      
      // Handle different response formats
      const items = result.items || result.data || result || [];
      const totalCount = result.total ?? items.length;
      
      setData(items);
      setTotal(totalCount);
    } catch (err) {
      setError(err);
      setData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [fetchFn, page, pageSize, enabled]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Reset to page 1 when dependencies change (when fetchFn changes due to filter changes)
  const prevFetchFnRef = useRef();
  useEffect(() => {
    if (prevFetchFnRef.current && prevFetchFnRef.current !== fetchFn) {
      if (page !== 1) {
        setPage(1);
      }
    }
    prevFetchFnRef.current = fetchFn;
  }, [fetchFn, page]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, totalPages);
  const from = total === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const to = Math.min(safePage * pageSize, total);

  const pagination = {
    page: safePage,
    pageSize,
    totalPages,
    total,
    from,
    to,
    onPageChange: setPage,
  };

  const reload = useCallback(() => {
    loadData();
  }, [loadData]);

  return { 
    data, 
    pagination, 
    loading, 
    error, 
    reload 
  };
}
